import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { wealthboxApi } from '@client/api/integrations';
import { useAuth } from '@clerk/react';

export function WealthboxOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const auth = useAuth();
  const advisorId = auth?.sessionClaims?.userId;

  const exchangeMutation = useMutation({
    mutationFn: (code: string) => wealthboxApi.oauth.exchangeCode({ code }),
    onSuccess: () => {
      if (advisorId) {
        navigate(`/advisors/${advisorId}/integrations`);
      } else {
        navigate('/');
      }
    },
  });

  useEffect(() => {
    if (code && auth.isLoaded) {
      exchangeMutation.mutate(code);
    }
  }, [code, auth.isLoaded]);

  if (!code) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-800">No authorization code found. Please try connecting again.</p>
        </div>
      </div>
    );
  }

  if (exchangeMutation.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="mb-2 text-lg font-semibold text-red-800">Connection Failed</p>
          <p className="text-sm text-red-700">
            {exchangeMutation.error instanceof Error
              ? exchangeMutation.error.message
              : 'Failed to connect to Wealthbox'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="border-t-primary mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300" />
        <p className="text-gray-700">Connecting to Wealthbox...</p>
      </div>
    </div>
  );
}
